import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSession } from '@/hooks/useSession';
import { ApiError } from '@/lib/api';
import { openBillingPortal, startCheckout, type PaidTier } from '@/lib/billing';
import { savePostAuthIntent } from '@/lib/post-auth-intent';
import { SALES_CONTACT_MAILTO, type PricingTierId } from '@/lib/waitlist';
import { PricingCard } from './PricingCard';

export interface PricingCardsProps {
  /** Where this is rendered — kept for analytics symmetry with the previous waitlist flow. */
  source: 'landing' | 'usage_tab' | 'over_quota_modal';
  /** Current tier the signed-in user is on (Settings > Usage). */
  currentTier?: PricingTierId;
  /** Override Free CTA navigation target. Defaults to `/sign-up`. */
  freeCtaTo?: string;
  /**
   * Whether the user has a Stripe Customer already (controls whether the
   * current-plan CTA opens the billing portal vs is disabled).
   */
  hasBillingCustomer?: boolean;
}

/**
 * 4-up pricing grid: Free / Pro / Scale / Custom.
 *
 * - Free CTA → navigates to sign-up (or `freeCtaTo`); becomes "Switch to Free"
 *   for paid users (opens Stripe Portal so cancel-at-period-end drops them
 *   to Free)
 * - Pro / Scale CTA → starts hosted Stripe Checkout
 * - Current-plan CTA → opens the Stripe Customer Billing Portal
 * - Custom CTA → mailto link (no Stripe involvement — sales conversation)
 *
 * Reused by:
 * - Landing page Pricing section (source=`landing`, no auth → just CTAs)
 * - Settings → Usage tab (source=`usage_tab`, with `currentTier`)
 * - Over-quota modal contexts (source=`over_quota_modal`)
 */
export function PricingCards({
  source,
  currentTier: currentTierProp,
  freeCtaTo = '/sign-up',
  hasBillingCustomer: hasBillingCustomerProp = false,
}: PricingCardsProps) {
  const navigate = useNavigate();
  const { user } = useSession();
  const [pending, setPending] = useState<PaidTier | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Caller may pass these explicitly (Settings does, since it has up-to-date
  // state from its own session refresh). On landing where the prop is unset,
  // we fall back to whatever /api/me told us so the cards render
  // contextually for logged-in visitors too.
  const currentTier = currentTierProp ?? user?.tier;
  const hasBillingCustomer = hasBillingCustomerProp || (user?.hasBillingCustomer ?? false);
  const isAuthenticated = !!user;

  async function handleUpgrade(tier: PaidTier) {
    // Landing-page / unauth flow: stash the intent and bounce to sign-up;
    // DashboardPage picks it up on landing and auto-launches Checkout. This
    // is the Resend / Cursor pattern — single click that survives sign-up.
    if (!isAuthenticated) {
      savePostAuthIntent({ kind: 'checkout', tier });
      navigate('/sign-up');
      return;
    }

    setError(null);
    setPending(tier);
    try {
      await startCheckout(tier);
      // We never get here — Checkout redirects away.
    } catch (err) {
      setPending(null);
      if (err instanceof ApiError) setError(err.message);
      else setError('Could not start checkout. Try again in a moment.');
    }
  }

  async function handleManageBilling() {
    setError(null);
    setPending('portal');
    try {
      await openBillingPortal();
    } catch (err) {
      setPending(null);
      if (err instanceof ApiError) setError(err.message);
      else setError('Could not open billing portal.');
    }
  }

  function ctaFor(tier: 'pro' | 'scale') {
    if (currentTier === tier) {
      // On their own plan — let them manage (cancel / change card) via portal.
      return hasBillingCustomer ? handleManageBilling : () => {};
    }
    return () => handleUpgrade(tier);
  }

  // Free-card behaviour:
  //   - Logged-out / on free  → "Start free" navigates to sign-up.
  //   - On paid w/ portal     → "Switch to Free" opens Stripe Portal (cancel
  //                             at period end → drops to Free).
  //   - On paid w/o portal    → shouldn't happen, but fall through to sign-up.
  const onPaidPlan = currentTier === 'pro' || currentTier === 'scale';
  const freeCardCta = onPaidPlan && hasBillingCustomer
    ? handleManageBilling
    : () => navigate(freeCtaTo);
  const freeCardLabel = onPaidPlan && hasBillingCustomer ? 'Switch to Free' : undefined;
  const freeCardLoading = onPaidPlan && pending === 'portal';

  // Suppress unused-var lint when source isn't currently routed anywhere.
  void source;

  return (
    <>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <PricingCard
          tier="free"
          current={currentTier === 'free'}
          ctaLabel={freeCardLabel}
          loading={freeCardLoading}
          onCta={freeCardCta}
        />
        <PricingCard
          tier="pro"
          // Pro is the default "Recommended" except for Scale users — showing
          // Pro as a recommendation to a Scale user would imply a downgrade.
          highlight={currentTier !== 'scale'}
          current={currentTier === 'pro'}
          loading={pending === 'pro' || (currentTier === 'pro' && pending === 'portal')}
          currentCtaLabel={hasBillingCustomer ? 'Manage billing' : 'Your current plan'}
          onCta={ctaFor('pro')}
        />
        <PricingCard
          tier="scale"
          highlight={currentTier === 'scale'}
          current={currentTier === 'scale'}
          loading={pending === 'scale' || (currentTier === 'scale' && pending === 'portal')}
          currentCtaLabel={hasBillingCustomer ? 'Manage billing' : 'Your current plan'}
          onCta={ctaFor('scale')}
        />
        <PricingCard tier="custom" ctaHref={SALES_CONTACT_MAILTO} />
      </div>

      {error ? (
        <Alert variant="destructive" className="mt-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
    </>
  );
}
